import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Lock, TrendingUp, TrendingDown, BarChart3, Fuel, Gem, Wheat, Flame } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type Quote = {
  symbol: string;
  name: string;
  category: string;
  price: number | null;
  change: number | null;
  changePercent: number | null;
  currency: string;
};

const COMMODITY_ICONS: Record<string, any> = {
  "BZ=F": Fuel, "CL=F": Fuel, "GC=F": Gem, "SI=F": Gem,
  "NG=F": Flame, "ZW=F": Wheat,
};

const formatPrice = (price: number | null, currency: string) => {
  if (price === null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(price);
};

const formatPct = (pct: number | null) => {
  if (pct === null) return "—";
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
};

const QuoteRow = ({ q, blurred }: { q: Quote; blurred: boolean }) => {
  const Icon = COMMODITY_ICONS[q.symbol] || BarChart3;
  const isUp = (q.changePercent ?? 0) >= 0;

  return (
    <div className={`grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1fr_100px_100px] items-center gap-2 sm:gap-3 px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-md transition-colors hover:bg-accent/50 ${blurred ? "blur-sm select-none" : ""}`}>
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex h-6 w-6 sm:h-7 sm:w-7 shrink-0 items-center justify-center rounded-md bg-secondary">
          <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground" />
        </div>
        <div className="min-w-0">
          <p className="text-xs sm:text-sm font-medium text-foreground truncate">{q.name}</p>
          <p className="text-[9px] sm:text-[10px] font-mono text-muted-foreground">{q.symbol}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-xs sm:text-sm font-mono font-semibold text-foreground">{formatPrice(q.price, q.currency)}</p>
      </div>
      <div className="text-right flex items-center justify-end gap-1">
        {isUp ? <TrendingUp className="h-3 w-3 text-green-400" /> : <TrendingDown className="h-3 w-3 text-red-400" />}
        <span className={`text-[10px] sm:text-xs font-mono font-semibold ${isUp ? "text-green-400" : "text-red-400"}`}>
          {formatPct(q.changePercent)}
        </span>
      </div>
    </div>
  );
};

const MarketRadar = () => {
  const { isPremium } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["market-data"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("fetch-market-data");
      if (error) throw error;
      return data as { quotes: Quote[]; fetchedAt: string };
    },
    staleTime: 300000, // 5 min
    refetchInterval: isPremium ? 300000 : 900000,
    retry: 2,
    placeholderData: (prev) => prev,
  });

  const commodities = (data?.quotes || []).filter((q) => q.category === "commodity");
  const indices = (data?.quotes || []).filter((q) => q.category === "index");

  // Conflict correlation: highlight oil if >2% move
  const oilAlert = commodities.find((q) => ["BZ=F", "CL=F"].includes(q.symbol) && Math.abs(q.changePercent ?? 0) > 2);

  return (
    <section id="market-radar" className="py-8">
      <div className="container mx-auto px-4">
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-1">
            <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            <h2 className="font-mono text-sm sm:text-lg font-bold uppercase tracking-wider text-foreground">Market Radar</h2>
          </div>
          {oilAlert && (
            <Badge variant="outline" className="text-[9px] font-mono text-red-400 border-red-400/30 bg-red-500/10 animate-pulse">
              ⚠ OIL {formatPct(oilAlert.changePercent)} — Conflict Indicator
            </Badge>
          )}
        </div>

        {isLoading && !data ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            Loading market data...
          </div>
        ) : (
          <Tabs defaultValue="commodities" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="commodities" className="text-xs">Commodities</TabsTrigger>
              <TabsTrigger value="indices" className="text-xs">Global Indices</TabsTrigger>
            </TabsList>

            <TabsContent value="commodities">
              <div className="rounded-lg border border-border bg-card overflow-hidden">
              <div className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1fr_100px_100px] gap-2 sm:gap-3 px-2.5 sm:px-3 py-2 text-[9px] sm:text-[10px] font-mono uppercase text-muted-foreground border-b border-border">
                  <span>Asset</span>
                  <span className="text-right">Price</span>
                  <span className="text-right">Change</span>
                </div>
                <div className="divide-y divide-border/50">
                  {commodities.map((q, i) => (
                    <QuoteRow key={q.symbol} q={q} blurred={!isPremium && i >= 3} />
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="indices">
              <div className="rounded-lg border border-border bg-card overflow-hidden">
                <div className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1fr_100px_100px] gap-2 sm:gap-3 px-2.5 sm:px-3 py-2 text-[9px] sm:text-[10px] font-mono uppercase text-muted-foreground border-b border-border">
                  <span>Index</span>
                  <span className="text-right">Price</span>
                  <span className="text-right">Change</span>
                </div>
                <div className="divide-y divide-border/50">
                  {indices.map((q, i) => (
                    <QuoteRow key={q.symbol} q={q} blurred={!isPremium && i >= 3} />
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}

        {!isPremium && (
          <div className="mt-3 rounded-md border border-primary/20 bg-primary/5 p-3 text-center">
            <p className="text-xs text-muted-foreground">
              <Lock className="mr-1 inline h-3 w-3" />
              Free users see 3 assets per category. <span className="text-primary font-semibold cursor-pointer">Upgrade to Premium</span> for full market data with 5-min refresh.
            </p>
          </div>
        )}

        {data?.fetchedAt && (
          <p className="mt-2 text-[10px] font-mono text-muted-foreground text-right">
            Last updated: {new Date(data.fetchedAt).toLocaleTimeString()}
          </p>
        )}
      </div>
    </section>
  );
};

export default MarketRadar;
